import React, { useState } from "react";
import axios from 'axios';
import {
  Settings,
  User,
  Sparkles,
  Database,
  Shield,
  FolderPlus,
  Zap,
  FileSpreadsheet,
  X,
  Mail,
  ShieldCheck,
  ArrowUpRight,
  Loader2,
  ArrowLeft,
  Lock,
  Search,
  LifeBuoy,
  Users,
  Building2,
  FileText,
  Trash2,
  ShieldAlert,
  Rocket,
  HelpCircle,
  Info,
  FolderOpen,
  ClipboardList,
  CreditCard,
  Sliders,
  Activity,
  Globe,
  Eye,
  EyeOff,
  LogOut,
  CheckCircle2,
  DownloadCloud,
  Check,
  Sun,
  Moon,
  Loader,
  RefreshCw,
} from "lucide-react";
import { PLANS } from "../../App";

const SettingsModal = ({
  showSettingsModal,
  setShowSettingsModal,
  user,
  darkMode,
  activities,
  supList,
  setDarkMode,
  allUsers,
  clearAuditLog,
  logout,
  isLoadingLogs,
  projects,
  fetchLogs,
  showTerms,
  setShowTerms,
  billingCycle,
  setBillingCycle,
  setAllUsers,
  updateUserRole,
  removeTeammate,
  storage,
  privacyMode,
  setPrivacyMode,
  handleStartMfaSetup,
  onDisable2FA,
  onMfaVerified,
}) => {
  const BACKEND_URL = "https://quopro-backend.onrender.com";

  const [activeTab, setActiveTab] = useState("profile");
  const [settingsTab, setSettingsTab] = useState("profile");
  const [hasReadTerms, setHasReadTerms] = useState(false);
  const [workspace, setWorkspace] = useState(null);

  // 1. Define State Hooks FIRST
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "timestamp",
    direction: "desc",
  });

  const [copiedId, setCopiedId] = useState(null);

  // 2. Define Filtered/Derived Data SECOND (using the state from above)
  const filteredActivities =
    activities
      ?.filter(
        (log) =>
          log.user?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          log.action?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          log.details?.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key])
          return sortConfig.direction === "asc" ? -1 : 1;
        if (a[sortConfig.key] > b[sortConfig.key])
          return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      }) || [];

  const downloadAuditLog = () => {
    // Now filteredActivities is accessible here!
    if (!filteredActivities || filteredActivities.length === 0) {
      alert("No logs available to export");
      return;
    }

    const headers = ["User", "Action", "Details", "Timestamp"];
    const rows = filteredActivities.map((log) => [
      `"${log.user || "System"}"`,
      `"${log.action}"`,
      `"${log.details?.replace(/"/g, '""')}"`,
      `"${new Date(log.timestamp).toLocaleString()}"`,
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((r) => r.join(",")),
    ].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `QuoPro_Audit_Log_${new Date().toISOString().split("T")[0]}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  //GENERAL STATES (Must be at the top, before any 'return null')
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showMfaSetup, setShowMfaSetup] = useState(false);
  const [mfaQrCode, setMfaQrCode] = useState("");
  const [mfaSecret, setMfaSecret] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  //CANCELLATION MODAL
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  //Logout MODAL
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout(); // Ensure your logout function is awaited if it's async
    } catch (error) {
      console.error("Logout failed", error);
      setIsLoggingOut(false); // Reset if there's an error
    }
  };

  if (!showSettingsModal) return null;

  const handleStartMfaFlow = async () => {
    try {
      setLoading(true);
      setError("");

      const data = await handleStartMfaSetup();

      if (data && data.qrCode) {
        console.log("Success! QR Code ready.");
        setMfaQrCode(data.qrCode);
        setMfaSecret(data.secret);
        setShowMfaSetup(true); // 🚩 This should trigger the UI swap
      } else {
        setError("Backend did not return a QR code.");
      }
    } catch (err) {
      setError(err.message || "Failed to initialise MFA");
    } finally {
      setLoading(false);
    }
  };

  // If modal is hidden, render nothing
  if (!showSettingsModal) return null;

  const closeSettings = () => setShowSettingsModal(false);

  // Helper for capacity (Moved inside to keep the component self-contained)
  const stats = [
    {
      label: "Project Slots",
      val: projects?.length || 0,
      max: user?.plan === "pro" ? 30 : user?.plan === "enterprise" ? "∞" : 2,
      color: "indigo",
    },
  ];

  const handleVerifyAndEnable = async () => {
    try {
      setIsVerifying(true);
      setError(""); // Clear any previous Level 6-8 (Red) errors

      const response = await fetch(
        `${BACKEND_URL}/api/security/verify-mfa-setup`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            token: verificationCode,
            userId: user.id, // Or user._id depending on your object
            secret: mfaSecret, // The secret we saved during the 'trigger' step
          }),
        }
      );

      const data = await response.json();

      if (data.success) {
        // 🚩 SUCCESS: Trigger the Green (Level 1-2) state update in App.js
        onMfaVerified();
        setShowMfaSetup(false);
        setVerificationCode("");
        // Add a small success toast or alert here if you have one
      } else {
        // 🚩 FAILURE: Trigger the Red (Level 6-8) error
        throw new Error(data.message || "Invalid verification code");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsVerifying(false);
    }
  };



  //BILLING DATE FUNCTIONS
  const getNextBillingDate = (createdAt, nextBillDate) => {
    // 1. PRIORITY: REAL BILLING DATE (FROM WEBHOOK / DB)
    // Ensure we check for actual content, not just existence
    if (nextBillDate && nextBillDate !== "" && nextBillDate !== null) {
      const dateObj = new Date(nextBillDate);

      // Check if the date is actually valid
      if (!isNaN(dateObj.getTime())) {
        return dateObj.toLocaleDateString("en-GB", {
          day: "numeric",
          month: "long",
          year: "numeric",
        });
      }
    }

    // 2. FALLBACK: CALCULATED DATE
    // If we don't have a creation date, we use 'now' as the anchor
    const anchorDate = createdAt ? new Date(createdAt) : new Date();

    // If the anchorDate itself is invalid, return a safe string
    if (isNaN(anchorDate.getTime())) return "Processing...";

    const now = new Date();

    // Create a date for THIS month, using the day from the anchor date
    let nextBilling = new Date(
      now.getFullYear(),
      now.getMonth(),
      anchorDate.getDate()
    );

    // If that date has already passed this month, move to next month
    if (nextBilling <= now) {
      nextBilling.setMonth(nextBilling.getMonth() + 1);
    }

    return nextBilling.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const handleThemeToggle = () => {
    const newMode = !darkMode;
    setDarkMode(newMode);

    if (newMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }

    const userKey = user.email.replace(/\./g, "_");
    const updatedUsers = { ...allUsers };

    if (updatedUsers[userKey]) {
      updatedUsers[userKey] = {
        ...updatedUsers[userKey],
        theme: newMode ? "dark" : "light",
      };
      setAllUsers(updatedUsers);

      // FIX: Pass ALL 8 arguments to match the storage.set definition
      storage.set(
        user.companyId, // 1
        projects, // 2
        updatedUsers, // 3
        activities, // 4
        supList, // 5
        user.plan, // 6
        user.email, // 7 (CRITICAL: Added this)
        newMode ? "dark" : "light" // 8 (CRITICAL: Added this)
      );
    }
  };

  const handleStart2FASetup = async () => {
    try {
      // 1. Call your existing backend route
      const response = await fetch(`${API_URL}/api/security/setup-mfa`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user.token}`, // Ensure you pass your auth token
        },
        body: JSON.stringify({ email: user.email }),
      });

      const data = await response.json();

      if (data.qrCode) {
        setTwoFactorQR(data.qrCode); // The base64 image from your backend
        setTwoFactorSecret(data.manualCode); // The backup code
        setIsSettingUp2FA(true); // Open the setup UI
      }
    } catch (err) {
      console.error("MFA Setup Failed:", err);
    }
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-6">
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-xl"
        onClick={closeSettings}
      />
      {/* Main Modal Container - Updated with Dark Mode Borders and BG */}
      <div className="relative bg-white dark:bg-slate-950 border border-transparent dark:border-slate-800 rounded-[3rem] shadow-2xl w-full max-w-7xl overflow-hidden flex h-[800px] animate-in fade-in zoom-in-95 duration-200">
        {/* SIDEBAR NAVIGATION */}
        <div className="w-64 bg-slate-100/70 dark:bg-slate-900/50 border-r border-slate-100 dark:border-slate-800 p-8 flex flex-col">
          <div className="flex items-center gap-3 mb-14 mt-1">
            <div className="w-10 h-10 bg-indigo-600 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-indigo-200 dark:shadow-none">
              <Settings size={20} />
            </div>
            <h2 className="font-black text-slate-900 dark:text-white text-xl tracking-tight" style={{
              filter: `
      /* Layer 1: Core definition (slightly crisper) */
      drop-shadow(0 0 1px rgba(15, 23, 42, 0.45)) 
      /* Layer 2: Mid-range body (darker for depth) */
      drop-shadow(0 0 3px rgba(165, 169, 179, 0.25)) 
      /* Layer 3: Outer soft glow (expanded for 5% boost) */
      drop-shadow(0 0 8px rgba(15, 23, 42, 0.15))`
            }}>
              Settings
            </h2>
          </div>

          <nav className="space-y-2 flex-1 text-slate-900">
            {[
              { id: "profile", label: "User Profile", icon: User },
              {
                id: "team",
                label: "Team Members",
                icon: Users,
                ownerOnly: true,
              },
              {
                id: "subscription",
                label: "Subscription",
                icon: Zap,
                ownerOnly: true,
              },
              {
                id: "billing",
                label: "Billing",
                icon: CreditCard,
                ownerOnly: true,
              },
              {
                id: "audit",
                label: "Audit Log",
                icon: ClipboardList,
                ownerOnly: true,
                action: fetchLogs,
              },
              {
                id: "security",
                label: "Security",
                icon: ShieldCheck,
                ownerOnly: false,
              },
            ].map((tab) => {
              if (tab.ownerOnly && !user?.isOwner && user?.plan !== "starter") return null;
              const Icon = tab.icon;
              const isActive = settingsTab === tab.id;

              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setSettingsTab(tab.id);
                    if (tab.action) tab.action();
                  }}
                  className={`text-slate-600 dark:text-slate-400 w-full flex items-center gap-3 px-4 py-3 rounded-xl font-bold text-sm transition-all duration-300 ease-out 
    ${isActive
                      ? "bg-white dark:bg-slate-800 shadow-sm text-indigo-600 dark:text-indigo-400 dark:hover:text-slate-200 pl-6 translate-x-1"
                      : "text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-500 hover:pl-6 hover:translate-x-1 "
                    }`}
                >
                  <Icon
                    size={18}
                    className={`transition-transform duration-300 ${isActive ? 'scale-110' : 'group-hover:scale-110'}`}
                  />
                  {tab.label}
                </button>
              );
            })}
          </nav>
          <button
            onClick={() => {
              setSettingsTab("help");
            }}
            className={`text-slate-600 dark:text-slate-400 w-full flex items-center gap-3 px-4 py-3 rounded-xl font-bold text-sm transition-all duration-300 ease-out 
    ${settingsTab === "help"
                ? "bg-white dark:bg-slate-800 shadow-sm text-indigo-600 dark:text-slate-400 dark:hover:text-slate-200 pl-6 translate-x-1"
                : "text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-500 hover:pl-6 hover:translate-x-1"
              }`}
          >
            <LifeBuoy size={18} className="transition-transform duration-300 group-hover:scale-110" />
            Help Guide
          </button>

          <button
            onClick={() => {
              setSettingsTab("terms");
              setShowTerms(true);
            }}
            className={`text-slate-600 dark:text-slate-400 w-full flex items-center gap-3 px-4 py-3 rounded-xl font-bold text-sm transition-all duration-300 ease-out 
    ${settingsTab === "terms"
                ? "bg-white dark:bg-slate-800 shadow-sm text-indigo-600 dark:text-slate-400 dark:hover:text-slate-200 pl-6 translate-x-1"
                : "text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-500 hover:pl-6 hover:translate-x-1"
              }`}
          >
            <FileText size={18} className="transition-transform duration-300 group-hover:scale-110" />
            T's & C's
          </button>

          <button
            onClick={() => setShowLogoutConfirm(true)}
            className="w-full flex items-center gap-3 px-4 py-3 text-red-400 hover:text-red-600 font-bold text-sm mt-auto transition-all duration-300 ease-out hover:pl-6 hover:translate-x-1"
          >
            <LogOut size={18} className="transition-transform duration-300 group-hover:scale-110" />
            Sign Out
          </button>
          {showLogoutConfirm && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300">
              <div className="w-full max-w-sm bg-white dark:bg-slate-900 rounded-[32px] p-8 shadow-2xl border border-slate-200 dark:border-white/10 scale-in-center">

                {/* Icon Header */}
                <div className="w-16 h-16 bg-red-50 dark:bg-red-500/10 rounded-2xl flex items-center justify-center mb-6 mx-auto">
                  <LogOut className="text-red-500" size={32} />
                </div>

                {/* Text Content */}
                <div className="text-center space-y-2 mb-8">
                  <h3 className="text-xl font-black text-slate-900 dark:text-white uppercase tracking-tight">
                    Confirm Sign Out
                  </h3>
                  <p className="text-sm font-bold text-slate-500 dark:text-slate-400">
                    Are you sure you want to end your current session?
                  </p>
                </div>

                {/* Actions */}
                <div className="grid grid-cols-2 gap-4">
                  <button
                    // Disable cancel while processing
                    disabled={isLoggingOut}
                    onClick={() => setShowLogoutConfirm(false)}
                    className={`py-4 rounded-2xl font-black text-xs uppercase tracking-widest transition-all bg-slate-100 dark:bg-white/5 
            ${isLoggingOut ? 'opacity-50 cursor-not-allowed' : 'text-slate-400 hover:text-slate-600 dark:hover:text-white'}`}
                  >
                    Cancel
                  </button>

                  <button
                    disabled={isLoggingOut}
                    onClick={handleLogout}
                    className="relative py-4 rounded-2xl font-black text-xs uppercase tracking-widest text-white bg-red-600 hover:bg-red-700 shadow-lg shadow-red-500/30 transition-all active:scale-95 flex items-center justify-center gap-2 overflow-hidden"
                  >
                    {isLoggingOut ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        <span className="font-bold">Signing Out...</span>
                      </>
                    ) : (
                      <span className="font-bold">Sign Out</span>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* MAIN CONTENT AREA */}
        <div className="flex-1 flex flex-col overflow-hidden bg-white dark:bg-slate-950">
          <div className="p-8 flex justify-between items-center border-b border-slate-50 dark:border-slate-900">
            <h3 className="text-3xl font-black text-slate-900 dark:text-white" style={{
              filter: `
      /* Layer 1: Core definition (slightly crisper) */
      drop-shadow(0 0 1px rgba(15, 23, 42, 0.45)) 
      /* Layer 2: Mid-range body (darker for depth) */
      drop-shadow(0 0 3px rgba(165, 169, 179, 0.25)) 
      /* Layer 3: Outer soft glow (expanded for 5% boost) */
      drop-shadow(0 0 8px rgba(15, 23, 42, 0.15))`
            }}>
              {settingsTab === "profile" && "Profile Overview"}
              {settingsTab === "security" && "Security Command Center"}
              {settingsTab === "team" && "Workspace Team Management"}
              {settingsTab === "subscription" && "Company Subscription"}
              {settingsTab === "terms" && "Terms & Conditions"}
              {settingsTab === "billing" && "Billing"}
              {settingsTab === "audit" && "Workspace Audit Log"}
              {settingsTab === "help" && "QuoPro Guide"}
            </h3>

            <button
              onClick={closeSettings}
              className="h-12 w-12 flex items-center justify-center text-slate-400 hover:bg-red-50 dark:hover:bg-red-500/10 hover:text-red-500 rounded-2xl transition-all"
            >
              <X size={24} />
            </button>
          </div>

          <div className="p-12 overflow-y-auto flex-1 custom-scrollbar">
            {/* TEAM MANAGEMENT VIEW */}
            {settingsTab === "team" && user?.isOwner && (
              <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div className="flex justify-between items-center">
                  <h4 className="text-[10px] font-black text-slate-900 dark:text-slate-300 uppercase tracking-widest">
                    Authorised Workspace Users
                  </h4>
                  <div className="flex items-center gap-2">
                    <ShieldCheck size={14} className="text-emerald-500" />
                    <span className="text-[10px] font-black text-emerald-500 uppercase tracking-widest">
                      Workspace Secured
                    </span>
                  </div>
                </div>

                <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-[2.5rem] overflow-hidden shadow-sm">
                  <table className="w-full text-left">
                    <thead className="bg-slate-50/50 dark:bg-slate-800/50">
                      <tr>
                        <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                          Full Name
                        </th>
                        <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                          Email Address
                        </th>
                        <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">
                          Role
                        </th>
                        <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50 dark:divide-slate-800">
                      {/* 1. Use a fallback to an empty array for entries to avoid crashes */}
                      {Object.keys(allUsers || {}).length > 0 ? (
                        Object.entries(allUsers).map(([safeKey, member]) => {
                          // Ensure email comparison is robust against case sensitivity or whitespace
                          const isCurrentUser = member?.email?.toLowerCase().trim() === user?.email?.toLowerCase().trim();

                          return (
                            <tr
                              key={safeKey}
                              className="group hover:bg-slate-100/30 dark:hover:bg-slate-800/30 transition-colors"
                            >
                              <td className="px-6 py-4">
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-full flex items-center justify-center text-[10px] font-black uppercase">
                                    {member?.name?.charAt(0) || "U"}
                                  </div>
                                  <span className="text-xs font-bold text-slate-900 dark:text-slate-200">
                                    {member?.name || "Unknown User"}
                                    {isCurrentUser && (
                                      <span className="text-indigo-500 ml-1 font-medium">(You)</span>
                                    )}
                                  </span>
                                </div>
                              </td>
                              <td className="px-6 py-4 text-xs font-bold text-slate-500 dark:text-slate-400">
                                {member?.email}
                              </td>
                              <td className="px-6 py-4 text-center">
                                <div className="flex flex-col items-center">
                                  {isCurrentUser ? (
                                    <span className="px-2 py-1 rounded-md text-[8px] font-black uppercase bg-amber-50 dark:bg-amber-900/20 text-amber-600 border border-amber-100 dark:border-amber-900/30">
                                      {member?.isOwner ? "Owner" : member?.role || "Member"}
                                      <span className="text-[7px] block opacity-70">(Your Session)</span>
                                    </span>
                                  ) : (
                                    <select
                                      value={member?.role || (member?.isOwner ? "Owner" : "Member")}
                                      onChange={(e) => updateUserRole(member?.email, e.target.value)}
                                      className="bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-lg border-none focus:ring-2 focus:ring-indigo-500 outline-none cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-700 transition-all text-center"
                                    >
                                      <option value="Owner">Owner</option>
                                      <option value="Lead">Lead</option>
                                      <option value="Manager">Manager</option>
                                      <option value="Member">Member</option>
                                    </select>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4 text-right">
                                {!isCurrentUser ? (
                                  <button
                                    onClick={() => removeTeammate(member?.email)}
                                    className="p-2 text-slate-300 dark:text-slate-600 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-lg transition-all"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                ) : (
                                  <span className="text-[9px] font-black text-slate-300 dark:text-slate-600 uppercase tracking-widest pr-2">
                                    Active
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        /* 2. Loading state - ONLY shows if the list is literally empty */
                        <tr>
                          <td colSpan="4" className="px-6 py-20 text-center">
                            <div className="flex flex-col items-center justify-center gap-3">
                              <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest animate-pulse">
                                Establishing Connection...
                              </p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="p-6 bg-slate-50 dark:bg-slate-900 rounded-[2rem] border border-dashed border-slate-200 dark:border-slate-800">
                  <p className="text-[10px] text-slate-400 font-bold leading-relaxed">
                    <Info size={12} className="inline mr-2 -mt-0.5" />
                    Team members are managed by Workspace ID:{" "}
                    <span className="text-indigo-600 dark:text-indigo-400 font-black">
                      {user?.companyId}
                    </span>
                    . Removing a user instantly revokes their access to all
                    workspace data.
                  </p>
                </div>
              </div>
            )}

            {/* HELP SECTION */}
            {settingsTab === "help" && (
              <div className="mt-[-20px] animate-in fade-in slide-in-from-bottom-4 duration-500">

                {/* 1. ADVANCED GLOBAL DOCUMENTATION HEADER */}
                <div className="p-10 border-b border-slate-100 dark:border-slate-800 flex justify-between items-start bg-slate-100/70 dark:bg-slate-900 rounded-[2.5rem] rounded-b-none z-10 transition-colors duration-300">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-2 h-2 rounded-full bg-indigo-600 dark:bg-indigo-400 animate-pulse shadow-[0_0_8px_#4f46e5]" />
                      <span className="text-[10px] font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-[0.3em]">
                        Knowledge Operations
                      </span>
                    </div>
                    <h4 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight" style={{
                      filter: `drop-shadow(0 0 1px rgba(15, 23, 42, 0.45)) drop-shadow(0 0 8px rgba(79, 70, 229, 0.15))`
                    }}>
                      QuoPro Platform Documentation
                    </h4>
                    <p className="text-[11px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest mt-2">
                      Master Architecture Guide & Operational Standards
                    </p>
                  </div>
                </div>

                {/* 2. MAIN TWO-COLUMN WORKSPACE FRAMEWORK */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 p-8 bg-slate-50/50 dark:bg-slate-900/40 border border-t-0 border-slate-100 dark:border-slate-800 rounded-b-[2.5rem] transition-colors duration-300">

                  {/* 🧭 LEFT STICKY QUICK-NAVIGATION DOCK */}
                  <div className="lg:col-span-4 lg:sticky lg:top-8 h-fit space-y-3">
                    <div className="p-4 bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm">
                      <span className="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest block mb-4 px-2">
                        System Modules
                      </span>
                      <nav className="space-y-1">
                        {[
                          { id: "projects", label: "01. Projects & Line Items" },
                          { id: "suppliers", label: "02. Vendor Management" },
                          { id: "quotes", label: "03. Importing Quotes" },
                          { id: "optimiser", label: "04. Cost Split Optimiser" },
                          { id: "workflow", label: "05. Workflow Pipelines" },
                          { id: "invoicing", label: "06. Invoicing Matrices" }
                        ].map((navItem) => (
                          <button
                            key={navItem.id}
                            onClick={() => {
                              document.getElementById(`help-${navItem.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }}
                            className="hover:ml-2 w-full text-left px-3 py-2 text-xs font-bold text-slate-500 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-slate-100/80 dark:hover:bg-slate-800/50 rounded-xl transition-all"
                          >
                            {navItem.label}
                          </button>
                        ))}
                      </nav>
                    </div>

                    {/* TEAM ASSISTANCE PANEL */}
                    <div className="hover:scale-[1.02] hover:shadow-lg p-5 bg-gradient-to-br from-indigo-900 to-slate-900 rounded-3xl border border-slate-800 text-white shadow-md">
                      <div className="flex items-center gap-2 text-indigo-400 mb-2">
                        <HelpCircle size={16} />
                        <span className="text-[10px] font-black uppercase tracking-widest">Direct Support</span>
                      </div>
                      <p className="text-xs text-slate-300 font-medium leading-relaxed mb-4">
                        Need advanced support or custom vendor script builds?
                      </p>
                      <a href="mailto:support@quopromanager.co.za" className="text-xs font-black text-white underline tracking-wide hover:text-indigo-300 transition-colors">
                        support@quopromanager.co.za
                      </a>
                    </div>
                  </div>

                  {/* 📚 RIGHT CONTENT EXPANSION PANEL */}
                  <div className="lg:col-span-8 space-y-6 max-h-[700px] overflow-y-auto pr-2 custom-scrollbar">

                    {/* VIDEO TUTORIAL HERO BANNER */}
                    <div className="flex flex-col md:flex-row gap-6 p-6 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-[2rem] shadow-sm group">
                      <div className="hover:scale-[1.02] hover:shadow-lg w-full md:w-56 aspect-video bg-slate-100 dark:bg-slate-950 rounded-2xl overflow-hidden relative shrink-0 border border-slate-100 dark:border-white/5">
                        <video
                          autoPlay
                          loop
                          muted
                          playsInline
                          className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
                          src="/videos/ref-match-tutorial.mp4"
                        />
                        <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/60 backdrop-blur-md rounded-lg text-[7px] font-black text-white uppercase tracking-widest">
                          Quick Overview
                        </div>
                      </div>
                      <div className="flex flex-col justify-center">
                        <h5 className="font-black text-slate-900 dark:text-white text-base mb-1">
                          Core Reference Architecture
                        </h5>
                        <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                          Learn how QuoPro links incoming vendor bids using matching reference codes. Keep references aligned to automate the optimisation process instantly.
                        </p>
                      </div>
                    </div>

                    {/* SYSTEM KNOWLEDGE NODE MATRICES */}
                    <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-[2.5rem] p-8 space-y-12">

                      {/* MODULE 1: PROJECTS */}
                      <section id="help-projects" className="space-y-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2.5 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 rounded-xl">
                            <FolderPlus size={18} />
                          </div>
                          <h4 className="text-base font-black text-slate-900 dark:text-white tracking-tight">
                            01. Adding Projects & Project Line Items
                          </h4>
                        </div>
                        <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed pl-11">
                          To launch an optimisation loop, construct a project cluster and define its line items. Every item requires a distinct **Ref Code**, description, and baseline volume (Qty). The Ref Code is the universal matching key that aligns incoming vendor bids.
                        </p>
                      </section>

                      <div className="h-px bg-slate-100 dark:bg-slate-800 ml-11" />

                      {/* MODULE 2: SUPPLIERS */}
                      <section id="help-suppliers" className="space-y-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2.5 bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 rounded-xl">
                            <Users size={18} />
                          </div>
                          <h4 className="text-base font-black text-slate-900 dark:text-white tracking-tight">
                            02. Supplier Management & B-BBEE Compliance
                          </h4>
                        </div>
                        <div className="pl-11 space-y-3">
                          <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                            Add profile configurations for suppliers, including international phone contacts and verified tax parameters. QuoPro handles South African B-BBEE rating layers dynamically across both dark and light visual modes:
                          </p>

                          {/* B-BBEE STYLING MATRIX */}
                          <div className="cursor:default grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
                            <div className="cursor-default p-3 rounded-xl bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-900/30 text-[10px] font-bold uppercase tracking-wide text-center">
                              🟢 Level 1 - 2 : Preferred
                            </div>
                            <div className="cursor-default p-3 rounded-xl bg-orange-50 dark:bg-orange-950/30 text-orange-700 dark:text-orange-400 border border-orange-100 dark:border-orange-900/30 text-[10px] font-bold uppercase tracking-wide text-center">
                              🟠 Level 3 - 5 : Balanced
                            </div>
                            <div className="cursor-default p-3 rounded-xl bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-400 border border-rose-100 dark:border-rose-900/30 text-[10px] font-bold uppercase tracking-wide text-center">
                              🔴 Level 6 - 8 : High Risk
                            </div>
                          </div>
                        </div>
                      </section>

                      <div className="h-px bg-slate-100 dark:bg-slate-800 ml-11" />

                      {/* MODULE 3: QUOTE INGESTION */}
                      <section id="help-quotes" className="space-y-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2.5 bg-sky-50 dark:bg-sky-900/20 text-sky-600 dark:text-sky-400 rounded-xl">
                            <FileSpreadsheet size={18} />
                          </div>
                          <h4 className="text-base font-black text-slate-900 dark:text-white tracking-tight">
                            03. Supplier Quote Records & Automated Import
                          </h4>
                        </div>
                        <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed pl-11">
                          Bypassing slow data entry fields is simple. Upload quote files directly using external document sheets or compile rows explicitly inside each vendor profile card. Ensure row indicators match your project keys to process cost comparisons instantly.
                        </p>
                      </section>

                      <div className="h-px bg-slate-100 dark:bg-slate-800 ml-11" />

                      {/* MODULE 4: THE OPTIMISER */}
                      <section id="help-optimiser" className="space-y-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2.5 bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 rounded-xl">
                            <Zap size={18} />
                          </div>
                          <h4 className="text-base font-black text-slate-900 dark:text-white tracking-tight">
                            04. Cost Split Optimiser Core Engine
                          </h4>
                        </div>
                        <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed pl-11">
                          The proprietary **Split Optimiser** compares your project needs against all loaded supplier bids. It automatically isolates the lowest line-item prices, mapping out a custom purchase matrix that tells you exactly which items to buy from which vendors to unlock maximum project savings.
                        </p>
                      </section>

                      <div className="h-px bg-slate-100 dark:bg-slate-800 ml-11" />

                      {/* MODULE 5: WORKFLOWS */}
                      <section id="help-workflow" className="space-y-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2.5 bg-pink-50 dark:bg-pink-900/20 text-pink-600 dark:text-pink-400 rounded-xl">
                            <Sliders size={18} />
                          </div>
                          <h4 className="text-base font-black text-slate-900 dark:text-white tracking-tight">
                            05. Procurement Workflow Pipelines
                          </h4>
                        </div>
                        <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed pl-11">
                          Track project milestones from the initial Request for Quote (RFQ) through to active selection adjustments. Change stage parameters dynamically to keep your team aligned on final award targets.
                        </p>
                      </section>

                      <div className="h-px bg-slate-100 dark:bg-slate-800 ml-11" />

                      {/* MODULE 6: INVOICING */}
                      <section id="help-invoicing" className="space-y-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2.5 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 rounded-xl">
                            <CreditCard size={18} />
                          </div>
                          <h4 className="text-base font-black text-slate-900 dark:text-white tracking-tight">
                            06. Invoicing Matrices & Document Compilation
                          </h4>
                        </div>
                        <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed pl-11">
                          Once optimisation is complete, compile clean invoices and split purchase summaries instantly. These files can be saved down as secure layout PDFs or routed to your external accounting pipelines.
                        </p>
                      </section>

                    </div>
                  </div>

                </div>
              </div>
            )}

            {/* TERMS SECTION */}
            {settingsTab === "terms" && (
              /* Removed space-y-6 to instantly close the margin gap between header and content */
              <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">

                {/* 1. HEADER SECTION */}
                <div className="p-10 border-b border-slate-100 dark:border-slate-800 flex justify-between items-start bg-white dark:bg-slate-900 rounded-[2.5rem] rounded-b-none z-10 transition-colors duration-300">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-2 h-2 rounded-full bg-indigo-600 dark:bg-indigo-400 animate-pulse shadow-[0_0_8px_#4f46e5]" />
                      <span className="text-[10px] font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-[0.3em]">
                        Legal Framework
                      </span>
                    </div>

                    <h4 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight" style={{
                      filter: `
            drop-shadow(0 0 1px rgba(15, 23, 42, 0.45)) 
            drop-shadow(0 0 3px rgba(165, 169, 179, 0.25)) 
            drop-shadow(0 0 8px rgba(79, 70, 229, 0.15))`
                    }}>
                      Terms of Service & Privacy Policy
                    </h4>

                    <p className="text-[11px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest mt-2">
                      QuoPro Legal & Privacy Policy Agreement
                    </p>
                  </div>
                </div>

                {/* 2. EXPANDED LEGAL CONTENT */}
                {/* Now sits completely flush against the header component above */}
                <div className="p-12 overflow-y-auto space-y-12 bg-slate-50/50 dark:bg-slate-900/40 border border-t-0 border-slate-100 dark:border-slate-800 rounded-b-[2.5rem] custom-scrollbar transition-colors duration-300">

                  {/* INTRODUCTION */}
                  <section className="max-w-3xl">
                    <p className="text-[13px] text-slate-600 dark:text-slate-400 leading-relaxed italic">
                      This Master Service Agreement ("Agreement") governs the access to and use of QuoPro’s cloud-based procurement optimisation platform. By accessing the platform, you acknowledge that you have read, understood, and agreed to be bound by the protocols defined herein.
                    </p>
                  </section>

                  {/* SECTION 1: PROVISION OF SERVICE */}
                  <section className="space-y-4">
                    <h5 className="text-[11px] font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-[0.2em]">
                      01. SCOPE OF ARCHITECTURE & SERVICE PROVISION
                    </h5>
                    <div className="grid md:grid-cols-2 gap-8">
                      <p className="text-[12px] text-slate-500 dark:text-slate-400 leading-relaxed">
                        QuoPro grants a non-exclusive, non-transferable right to access the platform. We maintain the absolute right to modify, update, or sunset specific algorithmic features to ensure optimal system performance and security compliance.
                      </p>
                      <p className="text-[12px] text-slate-500 dark:text-slate-400 leading-relaxed">
                        Service uptime is targeted at 99.9%, excluding scheduled maintenance windows or Force Majeure events including but not limited to regional internet backbone failures.
                      </p>
                    </div>
                  </section>

                  {/* SECTION 2: LIABILITY (HIGH VISIBILITY PRO CARD) */}
                  <section className="p-8 bg-white dark:bg-slate-900/60 rounded-[2rem] border border-slate-200 dark:border-slate-800 shadow-sm dark:shadow-inner space-y-4 transition-colors duration-300">
                    <h5 className="text-[11px] font-black text-rose-600 dark:text-rose-400 uppercase tracking-[0.2em]">
                      02. LIMITATION OF LIABILITY & INDEMNITY
                    </h5>
                    <p className="text-[12px] text-slate-600 dark:text-slate-300 leading-relaxed font-bold">
                      UNDER NO CIRCUMSTANCES SHALL QUOPRO, ITS DIRECTORS, OR AFFILIATES BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL OR PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION, LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM (I) YOUR ACCESS TO OR USE OF OR INABILITY TO ACCESS OR USE THE SERVICE; (II) ANY CONDUCT OR CONTENT OF ANY THIRD PARTY ON THE SERVICE.
                    </p>
                    <p className="text-[11px] text-slate-400 dark:text-slate-500 leading-relaxed">
                      In no event shall QuoPro's aggregate liability exceed the amounts actually paid by you to QuoPro for the service in the twelve (12) month period immediately preceding the event giving rise to such claim.
                    </p>
                  </section>

                  {/* SECTION 3: DATA PRIVACY & ENCRYPTION */}
                  <section className="space-y-4">
                    <h5 className="text-[11px] font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-[0.2em]">
                      03. DATA SOVEREIGNTY & INTELLECTUAL PROPERTY
                    </h5>
                    <div className="space-y-4 text-[12px] text-slate-500 dark:text-slate-400 leading-relaxed">
                      <p>
                        <strong className="text-slate-900 dark:text-slate-200 font-black">User Data:</strong> You retain all rights, title, and interest in the data you upload. QuoPro is granted a limited license to host and process this data solely for providing the service.
                      </p>
                      <p>
                        <strong className="text-slate-900 dark:text-slate-200 font-black">Platform IP:</strong> The QuoPro source code, UI logic, design patterns, and "Tunnel" background architecture are the exclusive property of QuoPro and protected by international copyright laws.
                      </p>
                    </div>
                  </section>

                  {/* SECTION 4: TERMINATION */}
                  <section className="space-y-4">
                    <h5 className="text-[11px] font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-[0.2em]">
                      04. TERMINATION OF ACCOUNT & DATA RETENTION
                    </h5>
                    <p className="text-[12px] text-slate-500 dark:text-slate-400 leading-relaxed">
                      Either party may terminate this agreement at any time. Upon termination, QuoPro will maintain your encrypted data for a period of 30 days, after which all records will be permanently purged from our active nodes unless a manual data-export is requested.
                    </p>
                  </section>

                  {/* FOOTER STAMP */}
                  <div className="pt-10 flex flex-col items-center justify-center text-center border-t border-slate-100 dark:border-slate-800">
                    <p className="text-[9px] font-medium text-slate-400 dark:text-slate-500 uppercase tracking-[0.4em]">
                      Protected by End-to-End Encryption & International Trade Law
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* SUBSCRIPTION SECTION */}
            {settingsTab === "subscription" && user?.isOwner && (
              <div className="space-y-12 animate-in fade-in slide-in-from-bottom-5 duration-700 pb-16">

                {/* --- SUBSCRIPTION PACKAGES VIEW --- */}
                <div className="flex flex-col items-center gap-4">
                  <div className="bg-slate-200/50 dark:bg-slate-900 p-1.5 rounded-[2rem] flex items-center gap-1 border border-slate-200 dark:border-slate-800 w-fit">
                    {["monthly", "yearly"].map((cycle) => (
                      <button
                        key={cycle}
                        onClick={() => setBillingCycle(cycle)}
                        className={`px-10 py-3 rounded-[1.5rem] text-[11px] font-black uppercase tracking-[0.15em] transition-all duration-300 relative ${billingCycle === cycle
                          ? "bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 shadow-xl scale-105"
                          : "text-slate-500 hover:text-slate-800 dark:hover:text-slate-300"
                          }`}
                      >
                        {cycle.charAt(0).toUpperCase() + cycle.slice(1)}
                        {cycle === "yearly" && (
                          <span className="absolute -top-3 -right-4 px-3 py-1 bg-emerald-500 text-white text-[9px] font-black rounded-full border-2 border-white dark:border-slate-950 animate-bounce shadow-md">
                            -15%
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
                  {PLANS.map((plan) => {
                    const isSelected = user?.plan === plan.id;
                    return (
                      <div
                        key={plan.id}
                        className={`group relative p-8 rounded-[2.5rem] border flex flex-col transition-all duration-500 shadow-2xl hover:scale-[1.04] ${isSelected
                          ? "border-indigo-500 bg-indigo-50/20 dark:bg-indigo-900/10 shadow-xl z-10 scale-[1.02]"
                          : "border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-slate-200 dark:hover:border-indigo-800"
                          }`}
                      >
                        {isSelected && (
                          <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-5 py-1 bg-indigo-600 text-white text-[9px] font-black uppercase tracking-[0.2em] rounded-full shadow-lg">
                            Current Plan
                          </div>
                        )}
                        <div className="text-center mb-8">
                          <p
                            className={`text-[10px] font-black uppercase tracking-[0.2em] mb-4 ${isSelected
                              ? "text-indigo-600 dark:text-indigo-400"
                              : "text-slate-400"
                              }`}
                          >
                            {plan.name}
                          </p>
                          <div className="flex items-baseline justify-center gap-1 cursor-default">
                            <span className="text-4xl font-black text-slate-900 dark:text-white tracking-tight" style={{ filter: 'drop-shadow(0 0 1px rgba(15,23,42,0.45))' }}>
                              {billingCycle === "monthly"
                                ? (plan.priceMonthly === "Free" || parseFloat(plan.priceMonthly) === 0 ? "" : "R") + plan.priceMonthly
                                : (plan.priceYearly === "Free" || parseFloat(plan.priceYearly) === 0 ? "" : "R") + plan.priceYearly}
                            </span>
                            <span className="text-[10px] font-bold text-slate-400 uppercase">
                              /{billingCycle === "monthly" ? "mo" : "yr"}
                            </span>
                          </div>
                        </div>
                        <ul className="space-y-4 mb-10 cursor-default">
                          {plan.features.map((feature, i) => (
                            <li
                              key={i}
                              className="cursor-default flex items-start gap-3 text-[11px] font-bold text-slate-500 dark:text-slate-400 leading-tight"
                            >
                              <CheckCircle2
                                size={14}
                                className={
                                  isSelected
                                    ? "text-indigo-500"
                                    : "text-emerald-500"
                                }
                              />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* BILLING SECTION */}
            {settingsTab === "billing" && user?.isOwner && (
              <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700 pb-12">
                <div className="text-center space-y-2">
                  <h3 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight" style={{
                    filter: `drop-shadow(0 0 1px rgba(15, 23, 42, 0.45)) drop-shadow(0 0 3px rgba(165, 169, 179, 0.25)) drop-shadow(0 0 8px rgba(15, 23, 42, 0.15))`
                  }}>
                    Billing & Subscription
                  </h3>
                  <p className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em]">
                    Manage your {user?.plan} workspace
                  </p>
                </div>

                {/* THE "IN-DEPTH" BILLING CARD */}
                <div className="bg-white dark:bg-slate-900 border border-slate-200/60 dark:border-slate-800 rounded-[3.5rem] p-10 shadow-2xl dark:shadow-none relative overflow-hidden">
                  {/* Subtle Lemon Yellow Glow Background */}
                  <div className="absolute top-0 right-0 w-64 h-64 bg-[#FFC233]/5 blur-[100px] -mr-32 -mt-32" />

                  <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center relative z-10">
                    {/* Left Side: Current Plan Info */}
                    <div className="lg:col-span-3 space-y-8">
                      <div className="flex items-start gap-6">
                        {/* Plan Icon with Lemon Accent */}
                        <div className="w-16 h-16 rounded-[2rem] bg-[#FFC233] flex items-center justify-center text-black shadow-xl shadow-amber-200/50 dark:shadow-none shrink-0 transition-transform duration-500 hover:rotate-6">
                          <ShieldCheck size={32} strokeWidth={2.5} />
                        </div>
                        <div>
                          <div className="flex items-center gap-3 mb-1">
                            <h4 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight capitalize">
                              {user?.plan || "Pro Manager"}
                            </h4>
                            <span className="px-3 py-1 bg-emerald-500 text-white text-[9px] font-black uppercase tracking-widest rounded-full">
                              Active
                            </span>
                          </div>
                          {/* NEXT BILLING CYCLE */}
                          <div className="flex flex-col gap-1">
                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                              Next Billing Cycle
                            </span>
                            <span className="text-slate-900 dark:text-slate-200 font-bold">
                              {getNextBillingDate(user?.createdAt, workspace?.nextBillDate)}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* ACTIONS */}
                      <div className="pt-4 flex flex-wrap items-center gap-4">
                        <button
                          onClick={() => setIsCancelModalOpen(true)}
                          className="group flex items-center gap-4 bg-slate-900 dark:bg-white dark:text-slate-900 text-white px-8 py-5 rounded-3xl font-black text-[11px] uppercase tracking-widest hover:bg-[#FFC233] hover:text-black transition-all shadow-xl shadow-slate-200 dark:shadow-none active:scale-95"
                        >
                          Update Subscription Details
                          <ArrowUpRight size={18} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                        </button>
                      </div>
                    </div>

                    {/* Right Side: Lemon Squeezy & Stripe Security Sidecar */}
                    <div className="lg:col-span-2 bg-slate-50 dark:bg-slate-800/50 rounded-[2.5rem] p-8 border border-slate-100 dark:border-slate-700">
                      <div className="space-y-6">
                        <div className="flex items-center gap-3 group">
                          <div className="relative">
                            <div className="absolute inset-0 bg-[#FFC233]/20 blur-lg rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                            <img
                              src="/LS.png"
                              alt="Lemon Squeezy Logo"
                              className="relative w-8 h-8 object-contain transition-transform duration-500 group-hover:rotate-6 group-hover:scale-110"
                            />
                          </div>
                          <h5 className="font-black text-slate-900 dark:text-white text-[10px] uppercase tracking-widest">
                            Billing Management
                          </h5>
                        </div>

                        <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed font-medium">
                          Subscriptions are handled via <span className="text-slate-900 dark:text-white font-bold">Lemon Squeezy</span>.
                          Financial data never touches our servers and is secured by <span className="text-[#635BFF] font-bold">Stripe</span> infrastructure.
                        </p>

                        <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
                          <div className="flex items-center justify-between pt-2">
                            <div className="flex flex-col gap-1">
                              <div className="flex items-center gap-1.5">
                                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                                <span className="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">
                                  Stripe Foundation
                                </span>
                              </div>
                              <p className="text-[8px] font-bold text-slate-300 dark:text-slate-600 uppercase tracking-tight">
                                PCI DSS Level 1 Certified
                              </p>
                            </div>

                            {/* Official Stripe Footer Logo */}
                            <div className="group/stripe flex flex-col items-center justify-center pt-2">
                              {/* Smaller, Stacked Label */}
                              <span className="text-[6px] font-black text-slate-400 uppercase tracking-[0.2em] leading-none mb-1 opacity-0 group-hover/stripe:opacity-100 transition-opacity duration-300">
                                Powered by
                              </span>

                              {/* Official Stripe Logo */}
                              <img
                                src="/Stripe.png"
                                alt="Stripe Logo"
                                className="w-10 h-auto object-contain opacity-60 grayscale hover:opacity-100 hover:grayscale-0 transition-all duration-500 ease-out group-hover/stripe:scale-105"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-[80px] pb-8">
                  <p className="text-center text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.25em]">
                    Certified Global Merchant Services via Lemon Squeezy & Stripe
                  </p>
                </div>
                {/* Increased margin to push the footer text further down */}


                {/* --- POLAR-THEMED CANCELLATION MODAL --- */}
                {isCancelModalOpen && (
                  <div className="fixed inset-0 z-[9999] p-4 flex items-center justify-center overflow-y-auto">
                    {/* Backdrop with Level 6-8 depth overlay */}
                    <div
                      className="fixed inset-0 bg-slate-950/40 dark:bg-slate-950/80 backdrop-blur-md transition-all duration-500 animate-in fade-in"
                      onClick={() => setIsCancelModalOpen(false)}
                    />

                    {/* Modal Shell */}
                    <div className="relative bg-white dark:bg-slate-900 w-full max-w-md rounded-[3rem] shadow-2xl dark:shadow-none overflow-hidden border border-slate-200/80 dark:border-slate-800 animate-in zoom-in-95 duration-300 z-10 my-auto">

                      {/* Brand Accent Top Bar - Level 3-5 Amber Accent Line */}
                      <div className="bg-[#FFC233] h-2 w-full" />

                      <div className="p-10">
                        <div className="flex flex-col items-center text-center w-full">

                          {/* Logo Section with Level 3-5 Lemon Glow */}
                          <div className="relative group mb-8">
                            <div className="absolute inset-0 bg-[#FFC233]/20 dark:bg-[#FFC233]/10 blur-2xl rounded-full opacity-100 transition-all duration-500 group-hover:scale-110" />
                            <div className="relative w-20 h-20 bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-3xl flex items-center justify-center shadow-lg">
                              <img
                                src="/LS.png"
                                alt="Lemon Squeezy Logo"
                                className="w-12 h-12 object-contain transition-transform duration-700 group-hover:rotate-6"
                              />
                            </div>
                          </div>

                          {/* Heading Text Components */}
                          <div className="max-w-[320px] space-y-3 mb-10">
                            <h3 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight leading-none" style={{
                              filter: `drop-shadow(0 0 1px rgba(15, 23, 42, 0.25))`
                            }}>
                              Billing Management
                            </h3>
                            <p className="text-slate-500 dark:text-slate-400 leading-relaxed text-xs font-bold px-2">
                              To update payments, change your current plan, or pause/cancel your subscription, you will be securely redirected to our verified merchant companion, <span className="text-[#FFC233] dark:text-amber-400 font-black">Lemon Squeezy</span>.
                            </p>
                          </div>
                        </div>

                        {/* Action Button Suite */}
                        <div className="flex flex-col gap-3">
                          {/* Main Direct Portal Target - Level 3-5 Accent State */}
                          <button
                            onClick={async () => {
                              // 1. Guard rail: If the URL is already present in state, use it instantly
                              if (user?.subscription?.updateUrl) {
                                window.open(user.subscription.updateUrl, "_blank");
                                setIsCancelModalOpen(false);
                                return;
                              }

                              // 2. Fallback: If it's undefined, fetch it live from the backend instead of breaking
                              try {
                                // Show a temporary loading state here if you have one
                                const API_BASE = "https://quopro-backend.onrender.com";
                                const response = await axios.post(`${API_BASE}/api/payments/portal-link`, {
                                  companyId: user?.companyId
                                });

                                if (response.data?.portalUrl) {
                                  window.open(response.data.portalUrl, "_blank");
                                  setIsCancelModalOpen(false);
                                } else {
                                  alert("Billing system link could not be generated. Please contact support.");
                                }
                              } catch (err) {
                                console.error("Portal Generation Error:", err);
                                alert("Failed to securely sync with the Lemon Squeezy gateway.");
                              }
                            }}
                            className="w-full bg-slate-900 hover:bg-[#FFC233] text-white hover:text-slate-950 dark:bg-white dark:hover:bg-[#FFC233] dark:text-slate-900 dark:hover:text-slate-950 py-5 rounded-2xl font-black text-[11px] uppercase tracking-[0.15em] transition-all duration-300 shadow-xl shadow-slate-950/10 dark:shadow-none active:scale-[0.97] flex items-center justify-center gap-2"
                          >
                            Go to Billing Portal
                            <ArrowUpRight size={16} strokeWidth={3} />
                          </button>

                          {/* Stay/Dismiss Option - Level 1-2 Protective Styling Action */}
                          <button
                            type="button"
                            onClick={() => setIsCancelModalOpen(false)}
                            className="w-full bg-slate-50 hover:bg-emerald-50 dark:bg-slate-800/40 dark:hover:bg-emerald-950/20 text-slate-500 hover:text-emerald-600 dark:text-slate-400 dark:hover:text-emerald-400 font-black py-4 rounded-2xl text-[10px] uppercase tracking-[0.2em] transition-all duration-300"
                          >
                            Nevermind, stay on Pro
                          </button>
                        </div>
                      </div>

                      {/* Trust Footer with Level 1-2 Safe Indicators & Stripe Branding */}
                      <div className="bg-slate-50 dark:bg-slate-800/30 px-10 py-6 flex items-center justify-between border-t border-slate-100 dark:border-slate-800">
                        <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
                          <Lock size={14} strokeWidth={2.5} />
                          <span className="text-[9px] font-black uppercase tracking-widest">
                            End-to-End Encryption
                          </span>
                        </div>

                        <div className="flex flex-col items-center justify-center select-none">
                          <span className="text-[6px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em] leading-none mb-1">
                            Powered by
                          </span>
                          <img
                            src="/Stripe.png"
                            alt="Stripe Logo"
                            className="w-10 h-auto object-contain opacity-60 dark:opacity-40 grayscale dark:invert-0"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

              </div>
            )}

            {/* AUDIT LOG SECTION - RESTORED BUTTONS */}
            {settingsTab === "audit" && user?.isOwner && (
              <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500 overflow-visible">

                {/* 🔒 STARTER PLAN PAYWALL GATEWAY */}
                {user?.plan === "starter" ? (
                  <div className="flex flex-col items-center justify-center text-center p-12 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-[2.5rem] shadow-sm min-h-[450px]">
                    <div className="w-16 h-16 bg-indigo-50 dark:bg-indigo-900/20 rounded-2xl flex items-center justify-center text-indigo-600 dark:text-indigo-400 mb-6 border border-indigo-100/30">
                      <ShieldAlert size={28} />
                    </div>

                    <h3 className="text-xl font-black text-slate-900 dark:text-white mb-2 tracking-tight">
                      Unlock the Audit Trail Feature
                    </h3>

                    <p className="text-xs text-slate-500 dark:text-slate-400 max-w-md leading-relaxed mb-8 font-medium">
                      Workspace log history and active team trace tracking are available exclusively on Pro and Business Plus tiers. Upgrade your workspace infrastructure to maintain complete data accountability.
                    </p>

                    <button
                      onClick={() => setSettingsTab("billing")} // Redirects user cleanly to your billing dashboard view
                      className="flex items-center gap-2 px-8 py-4 bg-slate-900 text-white rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] hover:bg-slate-900 hover:shadow-indigo-500/20 transition-all shadow-md "
                    >

                      Upgrade Your Workspace Plan
                    </button>
                  </div>
                ) : (
                  /* 🔓 FULL AUDIT SYSTEM VIEW (PRO & BUSINESS PLUS INTERFACES) */
                  <>
                    {/* ⚡️ STATS GRID */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                      {[
                        {
                          label: "Total Actions",
                          val: activities?.length || 0,
                          icon: <Activity size={16} />,
                          hint: "Total workspace events recorded.",
                        },
                        {
                          label: "Active Users",
                          val: [...new Set((activities || []).map(a =>
                            (typeof a.user === 'object' ? a.user?.name : a.user)?.toString().trim()
                          ).filter(Boolean))].length,
                          icon: <Users size={16} />,
                          hint: "Unique team members contributing",
                        },
                        {
                          label: "Critical Edits",
                          val: (activities || []).filter(a =>
                            ["delete", "clear", "wipe"].some(term => a.action?.toLowerCase().includes(term))
                          ).length,
                          icon: <ShieldAlert size={16} />,
                          hint: "Sensitive data deletion actions",
                        },
                      ].map((stat, i) => (
                        <div
                          key={i}
                          className="group relative bg-slate-50 dark:bg-slate-800/50 p-5 rounded-3xl border border-slate-100 dark:border-slate-800 transition-all hover:border-indigo-500/30"
                        >
                          <div className="flex justify-between items-center">
                            <div className="p-2 bg-white dark:bg-slate-900 rounded-xl shadow-sm text-indigo-500">
                              {stat.icon}
                            </div>
                            <div className="text-right">
                              <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block">
                                {stat.label}
                              </span>
                              <span className="text-xl font-black text-slate-900 dark:text-white">
                                {stat.val}
                              </span>
                            </div>
                          </div>
                          {/* Tooltip */}
                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 bg-slate-900 text-[10px] text-slate-200 rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity text-center z-50 shadow-xl border border-slate-700">
                            {stat.hint}
                            <div className="absolute top-full left-1/2 -translate-x-1/2 border-8 border-transparent border-t-slate-900" />
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* 🔍 SEARCH & SORT CONTROL BAR */}
                    <div className="flex flex-col md:flex-row justify-between items-center gap-4 bg-white dark:bg-slate-900 p-4 rounded-[2rem] border border-slate-100 dark:border-slate-800">
                      <div className="relative w-full md:w-96">
                        <Search
                          className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                          size={16}
                        />
                        <input
                          type="text"
                          placeholder="Search logs, users, or specific actions..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="w-full pl-12 pr-4 py-3 bg-slate-50 dark:bg-slate-800 border-none rounded-2xl text-xs font-medium focus:ring-2 focus:ring-indigo-500 transition-all"
                        />
                      </div>

                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest mr-2">
                          Sort By:
                        </span>
                        <button
                          onClick={() => requestSort("timestamp")}
                          className={`px-4 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all ${sortConfig.key === "timestamp"
                            ? "bg-indigo-600 text-white"
                            : "bg-slate-100 dark:bg-slate-800 text-slate-500"
                            }`}
                        >
                          Time{" "}
                          {sortConfig.key === "timestamp" &&
                            (sortConfig.direction === "asc" ? "↑" : "↓")}
                        </button>
                        <button
                          onClick={() => requestSort("user")}
                          className={`px-4 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all ${sortConfig.key === "user"
                            ? "bg-indigo-600 text-white"
                            : "bg-slate-100 dark:bg-slate-800 text-slate-500"
                            }`}
                        >
                          User{" "}
                          {sortConfig.key === "user" &&
                            (sortConfig.direction === "asc" ? "↑" : "↓")}
                        </button>
                      </div>
                    </div>

                    {/* TABLE SECTION */}
                    <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-[2.5rem] overflow-hidden shadow-sm flex flex-col min-h-[100px]">
                      {isLoadingLogs ? (
                        <div className="flex-1 flex flex-col items-center justify-center p-20 space-y-4">
                          <RefreshCw
                            className="animate-spin text-indigo-600"
                            size={32}
                          />
                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">
                            Syncing Logs...
                          </p>
                        </div>
                      ) : (
                        <table className="w-full text-left">
                          <thead className="bg-slate-50/50 dark:bg-slate-800/50">
                            <tr>
                              <th
                                className="px-6 py-5 text-[9px] font-black text-slate-400 uppercase tracking-widest w-[20%] cursor-pointer hover:text-indigo-500 transition-colors"
                                onClick={() => requestSort("user")}
                              >
                                User
                              </th>
                              <th className="px-6 py-5 text-[9px] font-black text-slate-400 uppercase tracking-widest w-[25%]">
                                Action
                              </th>
                              <th className="px-6 py-5 text-[9px] font-black text-slate-400 uppercase tracking-widest w-[40%]">
                                Details
                              </th>
                              <th
                                className="px-6 py-5 text-[9px] font-black text-slate-400 uppercase tracking-widest text-right w-[15%] cursor-pointer hover:text-indigo-500 transition-colors"
                                onClick={() => requestSort("timestamp")}
                              >
                                Time
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-50 dark:divide-slate-800">
                            {(filteredActivities || []).length > 0 ? (
                              filteredActivities.map((log, index) => (
                                <tr key={log._id || log.id || index} className="hover:bg-slate-50/30 dark:hover:bg-slate-800/30 transition-colors group hover:bg-slate-100/30">
                                  <td className="px-6 py-4">
                                    <div className="flex items-center gap-3">
                                      <div className="w-7 h-7 bg-indigo-100 dark:bg-indigo-900/40 rounded-full flex items-center justify-center text-[10px] font-black text-indigo-600">
                                        {log.user?.substring(0, 2).toUpperCase() ||
                                          "??"}
                                      </div>
                                      <span className="font-bold text-xs text-slate-900 dark:text-slate-200">
                                        {log.user || "System"}
                                      </span>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4">
                                    {(() => {
                                      const action = log.action?.toLowerCase() || "";
                                      let colorClass = "bg-slate-50 dark:bg-slate-900/20 text-slate-600 font-bold";

                                      if (["removed", "deleted", "cleared", "wiped"].some((word) => action.includes(word))) {
                                        colorClass = "bg-rose-50 dark:bg-rose-900/20 text-rose-600 font-bold";
                                      } else if (["added", "saved", "created", "import", "imported"].some((word) => action.includes(word))) {
                                        colorClass = "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 font-bold";
                                      } else if (["updated", "modified", "maintained"].some((word) => action.includes(word))) {
                                        colorClass = "bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 font-bold";
                                      } else if (["compliance", "login", "logout"].some((word) => action.includes(word))) {
                                        colorClass = "bg-orange-50 dark:bg-orange-900/20 text-orange-600 font-bold";
                                      } else if (["requested", "approved"].some((word) => action.includes(word))) {
                                        colorClass = "bg-purple-50 dark:bg-purple-900/20 text-purple-600 font-bold";
                                      }

                                      return (
                                        <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-widest transition-colors duration-300 ${colorClass}`}>
                                          {log.action}
                                        </span>
                                      );
                                    })()}
                                  </td>
                                  <td className="px-6 py-4 text-xs text-slate-500 dark:text-slate-400 font-medium transition-all">
                                    "{log.details}"
                                  </td>
                                  <td className="px-6 py-4 text-right text-[10px] font-black text-slate-400 uppercase">
                                    {log.timestamp
                                      ? new Date(log.timestamp).toLocaleDateString(
                                        undefined,
                                        {
                                          month: "short",
                                          day: "numeric",
                                          hour: "2-digit",
                                          minute: "2-digit",
                                        }
                                      )
                                      : "Recent"}
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="4" className="px-6 py-20 text-center">
                                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                    No matching activities found.
                                  </p>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      )}
                    </div>

                    {/* ACTIONS */}
                    <div className="flex justify-end items-center gap-3 w-full px-2">
                      <button
                        onClick={downloadAuditLog}
                        className="flex items-center gap-2 px-6 py-3 bg-white dark:bg-slate-900 text-indigo-600 dark:text-indigo-400 rounded-2xl text-[10px] font-black uppercase tracking-[0.15em] hover:bg-indigo-600 hover:text-white transition-all border border-slate-100 dark:border-slate-800 shadow-sm"
                      >
                        <DownloadCloud size={14} />
                        Export {searchTerm ? "Filtered" : "Full"} History
                      </button>
                      <button
                        onClick={clearAuditLog}
                        className="flex items-center gap-2 px-6 py-3 bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400 rounded-2xl text-[10px] font-black uppercase tracking-[0.15em] hover:bg-rose-600 hover:text-white transition-all"
                      >
                        <Trash2 size={14} /> Wipe Logs
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}



            {/* SECURITY SECTION */}
            {settingsTab === "security" && (
              <div className="space-y-10 animate-in fade-in slide-in-from-bottom-8 duration-700 pb-12 relative">
                {/* 🚩 THE SETUP OVERLAY MODAL */}
                {showMfaSetup && (
                  <div className="absolute inset-0 z-50 bg-white dark:bg-slate-950 flex flex-col items-center justify-center p-6 animate-in fade-in zoom-in-95 duration-300">
                    <div className="max-w-3xl w-full bg-white dark:bg-slate-950 rounded-[3rem] p-2">
                      {/* 1. TOP SECTION: Centered Header */}
                      <span>
                        <br />
                        <br />
                        <br />
                        <br />
                      </span>
                      <div className="text-center pt-24 pb-2 space-y-2">
                        <h4 className="text-[30px] font-bold text-slate-800 dark:text-white tracking-[-0.02em] leading-none mb-2">
                          Security Setup
                        </h4>
                        <p className="text-[9px] font-black text-slate-500 dark:text-orange-400 uppercase tracking-[0.2em] max-w-sm mx-auto leading-relaxed">
                          {/* Level 3-5 Orange: Action Required Styling */}
                          <br />
                          Please use <b>Microsoft Authenticator</b> or{" "}
                          <b>Google Authenticator</b> to scan the code to get a
                          6-digit code.
                        </p>
                      </div>
                      {/* 2. MIDDLE SECTION: Side-by-Side Content */}
                      <div className="flex flex-col md:flex-row items-center gap-16 p-12 pt-6">
                        {/* LEFT SIDE: QR Code */}
                        <div className="flex-1 flex flex-col items-center space-y-6">
                          <div className="relative group">
                            <div className="absolute -inset-4 bg-indigo-500/10 dark:bg-indigo-500/5 rounded-[3rem] blur-xl group-hover:blur-2xl transition-all duration-500"></div>
                            <div className="relative bg-white p-6 rounded-[2.5rem] shadow-2xl border-8 border-slate-50 dark:border-slate-900">
                              <img
                                src={mfaQrCode}
                                alt="MFA QR Code"
                                className="w-44 h-44 md:w-52 md:h-52"
                              />
                            </div>
                          </div>
                          <div className="text-center">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em]">
                              Step 1: Scan QR Code
                            </p>
                          </div>
                        </div>

                        {/* RIGHT SIDE: Controls */}
                        <div className="flex-1 w-full space-y-8 text-center">
                          <div className="space-y-4">
                            {error && (
                              <p className="text-[10px] font-black text-rose-500 uppercase tracking-widest animate-shake">
                                {/* Level 6-8 Red for Error */}
                                {error}
                              </p>
                            )}
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                              Step 2: Enter 6-Digit Code
                            </p>
                            <input
                              type="text"
                              placeholder="000 000"
                              maxLength={6}
                              value={verificationCode}
                              onChange={(e) =>
                                setVerificationCode(
                                  e.target.value.replace(/\D/g, "")
                                )
                              }
                              className={`w-full py-5 bg-slate-50 dark:bg-slate-900 border-2 rounded-2xl text-center text-3xl font-black tracking-[0.3em] outline-none transition-all ${verificationCode.length === 6
                                ? "border-emerald-500 text-emerald-500 shadow-lg shadow-emerald-500/10" // Level 1-2 Green
                                : "border-slate-100 dark:border-slate-800 focus:border-indigo-500 text-slate-900 dark:text-white"
                                }`}
                            />
                          </div>

                          <div className="flex gap-3">
                            <button
                              onClick={() => setShowMfaSetup(false)}
                              className="flex-1 py-4 bg-slate-100 dark:bg-slate-900 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-800 rounded-2xl text-[10px] font-bold uppercase tracking-widest hover:bg-rose-50 dark:hover:bg-rose-500/30 hover:text-rose-600 dark:hover:text-white hover:border-rose-200 dark:hover:border-rose-900/50 transition-all active:scale-95"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleVerifyAndEnable}
                              disabled={
                                verificationCode.length !== 6 || isVerifying
                              }
                              className="flex-[2] py-4 bg-indigo-600 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-lg shadow-indigo-500/20 disabled:opacity-30 hover:bg-indigo-700 transition-all"
                            >
                              {isVerifying
                                ? "Verifying..."
                                : "Confirm & Activate"}
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* 3. FOOTER: App Store Links */}
                      <div className="mt-4 pt-10 pb-6 flex flex-col gap-5 items-center border-t border-slate-50 dark:border-slate-900/50">
                        <p className="text-[8px] font-black text-slate-300 dark:text-slate-600 uppercase tracking-[0.4em]">
                          Need an authenticator app?
                        </p>
                        <div className="flex gap-4">
                          <a
                            href="https://apps.apple.com/za/app/authenticator/id6748213186"
                            target="_blank"
                            rel="noreferrer"
                            className="group bg-slate-950 border border-white/5 px-6 py-3 rounded-2xl flex items-center gap-3 hover:bg-black transition-all active:scale-95 shadow-2xl"
                          >
                            {/* ... SVG Icon ... */}
                            <div className="flex flex-col items-start leading-none">
                              <span className="text-[6px] text-white/40 uppercase font-black tracking-widest">
                                Available on
                                <br />
                              </span>
                              <span className="text-[10px] text-white font-black">
                                App Store
                              </span>
                            </div>
                          </a>
                          <a
                            href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2"
                            target="_blank"
                            rel="noreferrer"
                            className="group bg-slate-950 border border-white/5 px-6 py-3 rounded-2xl flex items-center gap-3 hover:bg-black transition-all active:scale-95 shadow-2xl"
                          >
                            {/* ... SVG Icon ... */}
                            <div className="flex flex-col items-start leading-none">
                              <span className="text-[6px] text-white/40 uppercase font-black tracking-widest">
                                Get it on
                                <br />
                              </span>
                              <span className="text-[10px] text-white font-black">
                                Google Play
                              </span>
                            </div>
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {/* THE MAIN SECURITY CARD */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div className="group relative p-8 bg-white dark:bg-slate-900 border border-slate-100 dark:border-white/5 rounded-[2.5rem] shadow-sm hover:shadow-xl hover:border-indigo-500/20 transition-all duration-500 overflow-hidden">
                    <div className="relative space-y-6">
                      <div className="flex justify-between items-center">
                        <div className="w-14 h-14 bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 rounded-2xl flex items-center justify-center shadow-inner">
                          <ShieldCheck size={28} strokeWidth={2.5} />
                        </div>
                        <span
                          className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest transition-all duration-300 ${user?.twoFactorEnabled
                            ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 border border-emerald-100/50" // Level 1-2 Green
                            : "bg-rose-50 dark:bg-rose-500/10 text-rose-600 border border-rose-100/50 dark:border-rose-800/50" // Level 6-8 Red
                            }`}
                        >
                          {user?.twoFactorEnabled ? "Protected" : "Unprotected"}
                        </span>
                      </div>

                      <div className="space-y-2">
                        <h4 className="text-lg font-black text-slate-900 dark:text-white tracking-tight">
                          Two-Factor Auth
                        </h4>
                        <p className="text-xs font-medium text-slate-500 dark:text-slate-400 leading-relaxed">
                          Add a secondary security layer to prevent unauthorised
                          workspace access.
                        </p>
                      </div>

                      <button
                        onClick={
                          user.twoFactorEnabled
                            ? onDisable2FA
                            : handleStartMfaFlow
                        }
                        disabled={loading}
                        className={`w-full py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all active:scale-95 shadow-lg flex items-center justify-center gap-2 ${user.twoFactorEnabled
                          ? "bg-slate-100 dark:bg-white/5 text-slate-600 dark:text-slate-300 hover:bg-rose-500 hover:text-white"
                          : "bg-slate-900 dark:bg-indigo-600 text-white hover:shadow-indigo-500/20"
                          } disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        {loading ? (
                          <>
                            <Loader className="animate-spin" size={14} />
                            <span className="font-bold">Generating QR...</span>
                          </>
                        ) : (
                          <span className="font-bold">
                            {user.twoFactorEnabled
                              ? "Deactivate Protection"
                              : "Setup Authenticator"}
                          </span>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* PROFILE SECTION */}
            {settingsTab === "profile" && (
              <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                {/* SUBSCRIPTION HEALTH DASHBOARD */}
                <div className="shadow-lg mt-[-30px] w-full p-6 md:p-8 bg-white/50 dark:bg-slate-900/30 backdrop-blur-xl rounded-[2rem] border border-slate-200/60 dark:border-slate-800/60">

                  {/* Section Header */}
                  <div className="mb-6 px-1">
                    <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">
                      Workspace Usage Metrics
                    </h4>
                  </div>

                  {(() => {
                    // 🟢 1. RESOLVE ACTIVE SYSTEM TIER DEFENSIVELY
                    const rawWorkspacePlan = workspace?.plan || "N/A";
                    const rawUserPlan = user?.plan || "N/A";
                    const activePlan = (workspace?.plan || user?.plan || "starter").toLowerCase().trim();

                    // 🔍 2. STREAMLINED DIAGNOSTIC LOG
                    console.log(`🕵️‍♂️ [QuoPro Debug] Workspace Plan: "${rawWorkspacePlan}" | User Plan: "${rawUserPlan}"`);

                    return (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {[
                          {
                            label: "Project Usage",
                            val: projects?.length || 0,
                            max: activePlan === "businessplus" ? Infinity : activePlan === "pro" ? 30 : 2,
                            icon: FolderOpen,
                            colorClass: "text-emerald-600 dark:text-emerald-400",
                            bgClass: "bg-emerald-50 dark:bg-emerald-500/10",
                            // 🟢 CHANGED: Fades from a translucent mist on the left to solid emerald on the right
                            barClass: "bg-gradient-to-r from-emerald-500/20 dark:from-emerald-400/10 to-emerald-500 dark:to-emerald-400",
                            borderClass: "",
                          },
                          {
                            label: "Supplier Network",
                            val: supList?.length || 0,
                            max: activePlan === "businessplus" ? Infinity : activePlan === "pro" ? 55 : 5,
                            icon: Globe,
                            colorClass: "text-sky-600 dark:text-sky-400",
                            bgClass: "bg-sky-50 dark:bg-sky-500/10",
                            // 🟢 CHANGED: Fades from translucent blue to solid sky blue
                            barClass: "bg-gradient-to-r from-sky-500/20 dark:from-sky-400/10 to-sky-500 dark:to-sky-400",
                            borderClass: "",
                          },
                          {
                            label: "Team Members",
                            val: Object.keys(allUsers || {}).length,
                            max: activePlan === "businessplus" ? Infinity : activePlan === "pro" ? 55 : 1,
                            icon: Users,
                            colorClass: "text-orange-600 dark:text-orange-400",
                            bgClass: "bg-orange-50 dark:bg-orange-500/10",
                            // 🟢 CHANGED: Fades from translucent orange to solid orange
                            barClass: "bg-gradient-to-r from-orange-500/20 dark:from-orange-400/10 to-orange-500 dark:to-orange-400",
                            borderClass: "",
                          },
                        ].map((stat, i) => {
                          const isUnlimited = stat.max === Infinity;
                          const progressWidth = isUnlimited ? 100 : Math.min((stat.val / stat.max) * 100, 100);

                          return (
                            <div
                              key={i}
                              className={`group bg-white dark:bg-slate-900/60 border border-slate-100 dark:border-slate-800/80 rounded-2xl p-6 shadow-sm transition-all duration-300 hover:shadow-md dark:hover:shadow-none ${stat.borderClass} hover:scale-[1.05]`}
                            >
                              <div className="flex justify-between items-start mb-6">
                                {/* Icon Wrapper Frame */}
                                <div className={`w-10 h-10 ${stat.bgClass} ${stat.colorClass} rounded-xl flex items-center justify-center shadow-inner transition-transform duration-300 group-hover:scale-105 font-bold`}>
                                  {stat.icon && <stat.icon size={18} strokeWidth={2} />}
                                </div>

                                {/* Counter Metric Display */}
                                <div className="text-right font-bold">
                                  <p className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em] mb-1.5">
                                    {stat.label}
                                  </p>

                                  <div className="text-xl font-semibold text-slate-900 dark:text-white leading-none flex items-center justify-end gap-1.5">
                                    <span className="font-bold">{stat.val}</span>

                                    {/* Maximum Cap Conditionals */}
                                    {stat.label !== "Team Members" && (
                                      <span className="font-bold text-slate-300 dark:text-slate-700 flex items-center text-sm gap-1">
                                        <span className="font-bold">/</span>
                                        {isUnlimited ? (
                                          /* 🟢 CRITICAL FIX: Safe, raw inline SVG path for Infinity symbol to bypass import crashes */
                                          <svg
                                            viewBox="0 0 24 24"
                                            width="16"
                                            height="16"
                                            stroke="currentColor"
                                            strokeWidth="2.5"
                                            fill="none"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            className="inline-block text-slate-400 dark:text-slate-500"
                                          >
                                            <path d="M12 12c-2-2.67-4-4-6-4a4 4 0 1 0 0 8c2 0 4-1.33 6-4zm0 0c2 2.67 4 4 6 4a4 4 0 1 0 0-8c-2 0-4 1.33-6 4z" />
                                          </svg>
                                        ) : (
                                          stat.max
                                        )}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Progress Track Strip */}
                              <div className="w-full h-1.5 bg-slate-100 dark:bg-slate-800/60 rounded-full overflow-hidden relative">
                                <div
                                  className={`h-full ${stat.barClass} rounded-full transition-all duration-1000 ease-out`}
                                  style={{ width: `${progressWidth}%` }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
                <div className="shadow-lg relative w-full p-6 md:p-8 bg-white/50 dark:bg-slate-900/30 backdrop-blur-xl rounded-3xl border border-slate-200/60 dark:border-slate-800/60 overflow-hidden transition-all duration-300">

                  {/* Ambient Architectural Accent Spill */}
                  <div className="absolute inset-0 pointer-events-none" />

                  <div className="flex flex-col md:flex-row items-center md:items-start gap-6 md:gap-8 relative z-10">

                    {/* 1. AVATAR HUB WITH ALIGNED INDICATORS */}
                    <div className="relative shrink-0">
                      <div className="w-20 h-20 md:w-24 md:h-24 bg-gradient-to-br from-indigo-500 via-indigo-600 to-indigo-700 rounded-2xl flex items-center justify-center text-white text-3xl font-black shadow-[0_16px_32px_-8px_rgba(79,70,229,0.3)] select-none">
                        {user?.name ? String(user.name).charAt(0).toUpperCase() : "U"}
                      </div>

                      {/* Real-time Presence Engine Pin */}
                      <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-white dark:bg-slate-950 rounded-full flex items-center justify-center shadow-sm">
                        <div className="w-3 h-3 bg-emerald-500 rounded-full shadow-[0_0_10px_rgba(16,185,129,0.5)]" />
                      </div>
                    </div>

                    {/* 2. CORE COMPONENT METRICS INFRASTRUCTURE */}
                    <div className="flex-1 w-full flex flex-col text-center md:text-left pt-0.5">

                      {/* Identity Label Pairing Block */}
                      <div className="flex flex-col gap-2 mb-6">
                        {/* 🟢 CHANGED: Added w-full and swapped md:justify-start for md:justify-between */}
                        <div className="flex flex-col md:flex-row md:items-center gap-3 justify-center md:justify-between w-full">

                          {/* Left Group: Name + Badge locked together */}
                          <div className="flex flex-col md:flex-row md:items-center gap-3 justify-center md:justify-start">
                            <h4 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight leading-tight">
                              {user?.name || "Anonymous User"}
                            </h4>

                            <div className="inline-flex items-center self-center md:self-auto px-2.5 py-0.5 bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 rounded-full border border-indigo-100/60 dark:border-indigo-500/20 select-none">
                              <span className="text-[9px] font-black uppercase tracking-[0.15em] whitespace-nowrap">
                                {user?.isOwner ? "Owner" : "Member"}
                              </span>
                            </div>
                          </div>

                          {/* Right Group: Pushed to the far right side on desktop */}
                          <div className="flex items-center justify-center md:justify-end gap-2 text-slate-400 dark:text-slate-500">
                            {Building2 && <Building2 size={12} className="opacity-70" />}
                            <span className="text-[9px] font-bold uppercase tracking-[0.12em] leading-none">
                              Active Instance:
                              <span className="font-extrabold text-indigo-600 dark:text-indigo-400 ml-1.5">
                                {user?.companyId ? String(user.companyId).replace(/-/g, " ").toUpperCase().trim() : "DEFAULT"} WORKSPACE
                              </span>
                            </span>
                          </div>

                        </div>

                        {/* System Identifier Directory Trace */}

                      </div>

                      {/* 3. PERMISSIONS AND COMMUNICATION SUB-GRID */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-6 border-t border-slate-100 dark:border-slate-800/60">

                        {/* Channel Routing Detail */}
                        <div className="flex items-center gap-3.5 p-3 rounded-xl bg-slate-100/70 dark:bg-slate-900/40 border border-slate-100/50 dark:border-slate-800/40 group transition-all duration-300">
                          <div className="w-9 h-9 shrink-0 rounded-lg bg-white dark:bg-slate-800 shadow-sm flex items-center justify-center text-slate-400 group-hover:text-indigo-500 dark:group-hover:text-indigo-400 transition-colors duration-300">
                            {Mail && <Mail size={15} />}
                          </div>
                          <div className="flex flex-col text-left min-w-0">
                            <span className="text-[8px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-0.5">Contact Link</span>
                            <span className="text-[12px] font-bold text-slate-700 dark:text-slate-200 truncate">
                              {user?.email || "No email provided"}
                            </span>
                          </div>
                        </div>

                        {/* Security / Clearances Access */}
                        <div className="flex items-center gap-3.5 p-3 rounded-xl bg-slate-100/70 dark:bg-slate-900/40 border border-slate-100/50 dark:border-slate-800/40 group transition-all duration-300">
                          <div className="w-9 h-9 shrink-0 rounded-lg bg-white dark:bg-slate-800 shadow-sm flex items-center justify-center text-slate-400 group-hover:text-indigo-500 dark:group-hover:text-indigo-400 transition-colors duration-300">
                            {ShieldCheck && <ShieldCheck size={15} />}
                          </div>
                          <div className="flex flex-col text-left min-w-0">
                            <span className="text-[8px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-0.5">Security Level</span>
                            <span className="text-[12px] font-bold text-slate-700 dark:text-slate-200 truncate">
                              {user?.isOwner ? "Administrative Access" : "Standard Team Access"}
                            </span>
                          </div>
                        </div>

                      </div>
                    </div>
                  </div>
                </div>
                {/* Theme Toggle in Profile */}
                <div className="shadow-lg z-50 p-6 bg-white dark:bg-slate-900/40 border border-slate-100 dark:border-slate-800 rounded-[2.5rem] flex items-center justify-between hover:shadow-xl">
                  <div className="flex items-center gap-4">
                    <div
                      className={`w-12 h-12 rounded-2xl flex items-center justify-center ${darkMode
                        ? "bg-indigo-500/10 text-indigo-400"
                        : "bg-amber-50 text-amber-600"
                        }`}
                    >
                      {darkMode ? <Moon size={20} /> : <Sun size={20} />}
                    </div>
                    <div>
                      <p className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-wider">
                        {darkMode ? "Dark Mode" : "Light Mode"}
                      </p>
                      <p className="text-[10px] font-bold text-slate-400">
                        Appearance Preference
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleThemeToggle}
                    className={`hover:shadow-sm w-14 h-8 rounded-full transition-all relative ${darkMode
                      ? "bg-indigo-600"
                      : "bg-slate-200 dark:bg-slate-700"
                      }`}
                  >
                    <div
                      className={`hover:scale-[1.01] absolute top-1 w-6 h-6 bg-white rounded-full transition-all shadow-md flex items-center justify-center ${darkMode ? "left-7" : "left-1"
                        }`}
                    >
                      {darkMode ? (
                        <Moon size={10} className="text-indigo-600" />
                      ) : (
                        <Sun size={10} className="text-amber-500" />
                      )}
                    </div>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;
